import {
  TicketNode,
  $createTicketNode,
} from '@/components/editor/TicketPlugin/TicketNode';
import {
  INSERT_TICKET_COMMAND,
  useTicketsStore,
} from '@/components/editor/TicketPlugin/TicketPlugin';
import {
  $createPlaceholderTicketNode,
  PlaceholderTicketNode,
} from '@/components/editor/TicketPlugin/PlaceholderTicketNode';
import TypeaheadPlugin from '@/components/editor/TypeaheadPlugin';
import {
  getTicketNumberClasses,
  TicketStatus,
  getTicketLineThroughClasses,
} from '@/lib/ticket-styles';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { MenuOption } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { $wrapNodeInElement, mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $insertNodes,
  $isRootOrShadowRoot,
  COMMAND_PRIORITY_EDITOR,
} from 'lexical';
import { useEffect, useMemo } from 'react';
import { Ticket } from '@certhon/domain-models';
import { cn } from '@/lib/utils';

export function TicketTypeaheadPlugin() {
  const [editor] = useLexicalComposerContext();
  const ticketStore = useTicketsStore();

  useEffect(() => {
    if (!editor.hasNodes([TicketNode])) {
      throw new Error(
        'TicketTypeaheadPlugin: TicketNode not registered on editor',
      );
    }
    if (!editor.hasNodes([PlaceholderTicketNode])) {
      throw new Error(
        'TicketTypeaheadPlugin: PlaceholderTicketNode not registered on editor',
      );
    }

    return mergeRegister(
      editor.registerCommand<{ ticket: Ticket; showTitle: boolean }>(
        INSERT_TICKET_COMMAND,
        ({ ticket, showTitle }) => {
          const ticketId = ticket.number?.toString() || ticket.id;
          const ticketNode = $createTicketNode(ticketId, showTitle);
          $insertNodes([ticketNode]);
          if ($isRootOrShadowRoot(ticketNode.getParentOrThrow())) {
            $wrapNodeInElement(ticketNode, $createParagraphNode).selectEnd();
          }
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    );
  }, [editor]);

  // Get all tickets (already deduplicated in the store)
  const ticketsList = ticketStore.getAllTickets();

  const options = useMemo(() => {
    const ticketOptions = ticketsList.map((t) => {
      const displayNumber = t.number ?? t.recnum ?? `temp-${t.id.slice(0, 8)}`;
      const option = new MenuOption(`ticket-${t.id}`);
      return Object.assign(option, {
        value:
          typeof displayNumber === 'number' ? displayNumber : displayNumber,
        keywords: [t.number?.toString() ?? '', t.title.toLowerCase()],
        ticket: t,
        title: t.title,
        number: displayNumber,
        status: t.status,
        isCreateOption: false,
        onSelect: () => {
          editor.dispatchCommand(INSERT_TICKET_COMMAND, {
            ticket: t,
            showTitle: true,
          });
        },
      });
    });

    // Add "Create new ticket" option at the end (always visible)
    const createOption = new MenuOption('create-new-ticket');
    const createNewOption = Object.assign(createOption, {
      value: 'create-new',
      keywords: [],
      alwaysShow: true, // Always show this option regardless of filter
      isCreateOption: true,
    });

    return [...ticketOptions, createNewOption];
  }, [ticketsList, editor]);

  return (
    <TypeaheadPlugin
      options={options}
      renderItem={(t, queryString) => {
        if ('isCreateOption' in t && t.isCreateOption) {
          return (
            <span className="flex w-full items-center gap-2">
              <span className="text-blue-600">+</span>
              <span className="text-blue-600">
                Create new ticket:{' '}
                <strong>{queryString || 'New Ticket'}</strong>
              </span>
            </span>
          );
        }

        const status = 'status' in t ? (t.status as TicketStatus) : undefined;
        return (
          <span
            className={`flex w-full items-center justify-between gap-2
            ${ticketStore.isLoading ? 'opacity-50' : ''}`}
          >
            <span className="flex min-w-0 items-center gap-1">
              <span className={getTicketNumberClasses(status)}>
                #{'number' in t ? t.number : ''}
              </span>
              <span
                className={cn(
                  'truncate',
                  getTicketLineThroughClasses(
                    'status' in t ? t.status : 'opened',
                  ),
                )}
              >
                {'title' in t ? t.title : ''}
              </span>
            </span>
          </span>
        );
      }}
      selectItem={(t, editor, queryString) => {
        if ('isCreateOption' in t && t.isCreateOption) {
          editor.update(() => {
            const placeholderNode = $createPlaceholderTicketNode(
              queryString || 'New Ticket',
            );
            $insertNodes([placeholderNode]);
            if ($isRootOrShadowRoot(placeholderNode.getParentOrThrow())) {
              $wrapNodeInElement(
                placeholderNode,
                $createParagraphNode,
              ).selectEnd();
            }
          });
        } else if ('onSelect' in t && t.onSelect) {
          t.onSelect();
        }
      }}
      trigger="#"
    />
  );
}
